import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HEALTH_PATH } from '../constants';
import { ApiHealthService } from '../services/api-health.service';

/**
 * HTTP Error Notification Interceptor
 *
 * Intercepts uncaught HTTP errors and displays user-friendly notifications
 * using ng-zorro notification service with translatable messages.
 *
 * Features:
 * - Catches all HTTP errors except 401 (handled by RefreshTokenInterceptor)
 * - Shows different notification types based on error status
 * - Provides pretty console logging for debugging
 * - Uses translatable error messages
 * - Configurable error exclusions
 */

// Error status codes to exclude from notifications
const EXCLUDED_STATUS_CODES = new Set([401, 0]); // 401 handled by RefreshTokenInterceptor, 0 is network/server down

// API endpoints to exclude from error notifications (if needed)
const EXCLUDED_ENDPOINTS = new Set<string>([
  // Add specific endpoints here if needed
  '/api/auth/refresh', // Handled by RefreshTokenInterceptor
  '/api/auth/refresh-token',
]);

// Network error codes that indicate server is down
const CONNECTION_ERROR_CODES = new Set([
  0, // Network error (ERR_CONNECTION_REFUSED, ERR_NAME_NOT_RESOLVED, etc.)
]);

// HTTP status codes that suggest server issues
const SERVER_DOWN_STATUS_CODES = new Set([
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
]);

// Track error frequency to avoid spam when server is down
let errorCount = 0;
let lastErrorTime = 0;
const ERROR_THROTTLE_MS = 10000; // Only show one error notification per 10 seconds
const MAX_ERRORS_BEFORE_QUIET = 5; // After 5 errors, reduce notification frequency

/**
 * Handles HTTP error responses
 */
function handleHttpError(
  error: HttpErrorResponse,
  requestUrl: string,
  requestMethod: string,
  requestBody: unknown,
  notificationService: NzNotificationService,
  translateService: TranslateService,
  apiHealthService: ApiHealthService,
): void {
  // Check if this is a connection error or server down - notify health service
  // Skip health endpoint itself to avoid recursion
  if (!requestUrl.includes(HEALTH_PATH) && isServerDownError(error)) {
    apiHealthService.markAsUnavailable('api-server-unavailable');
  }

  // Skip excluded status codes
  if (EXCLUDED_STATUS_CODES.has(error.status)) {
    return;
  }

  // Skip excluded endpoints
  if (isExcludedEndpoint(requestUrl)) {
    return;
  }

  // Check if we should throttle notifications (server down scenario)
  if (!shouldShowNotification(error)) {
    // Still log to console for debugging, but don't spam notifications
    if (shouldLogError()) {
      logError(error, requestUrl, requestMethod, requestBody);
    }
    return;
  }

  // Log error with pretty formatting
  logError(error, requestUrl, requestMethod, requestBody);

  // Show notification based on error type
  showErrorNotification(error, notificationService, translateService);
}

/**
 * Handles generic errors (non-HTTP)
 */
function handleGenericError(
  error: unknown,
  requestUrl: string,
  requestMethod: string,
  notificationService: NzNotificationService,
  translateService: TranslateService,
): void {
  // Log the error
  if (!environment.production) {
    console.group('🔥 Generic Error');
    console.error('Error:', error);
    console.log('Request URL:', requestUrl);
    console.log('Request Method:', requestMethod);
    console.groupEnd();
  }

  // Show generic error notification
  const title = translateService.instant('errors.generic.title') || 'Unexpected Error';
  const message = translateService.instant('errors.generic.message') || 'An unexpected error occurred. Please try again.';

  notificationService.error(title, message, {
    nzPlacement: 'bottomRight',
    nzDuration: 5000,
  });
}

/**
 * Pretty console logging for HTTP errors
 */
function logError(error: HttpErrorResponse, requestUrl: string, requestMethod: string, requestBody: unknown): void {
  if (!environment.production) {
    const errorType = getErrorType(error.status);
    const emoji = getErrorEmoji(error.status);

    console.group(`${emoji} HTTP ${error.status} Error - ${errorType}`);
    console.error('Error Response:', error);
    console.log('📍 URL:', requestUrl);
    console.log('🔧 Method:', requestMethod);
    console.log('📊 Status:', `${error.status} ${error.statusText}`);

    if (error.error) {
      console.log('💬 Error Message:', error.error.message || error.message);
    }

    if (requestBody) {
      console.log('📦 Request Body:', requestBody);
    }

    console.log('⏰ Timestamp:', new Date().toISOString());
    console.groupEnd();
  }
}

/**
 * Shows appropriate notification based on error status
 */
function showErrorNotification(
  error: HttpErrorResponse,
  notificationService: NzNotificationService,
  translateService: TranslateService,
): void {
  const { title, message, type } = getErrorNotificationConfig(error, translateService);

  const notificationOptions = {
    nzPlacement: 'bottomRight' as const,
    nzDuration: type === 'error' ? 8000 : 5000, // Longer duration for errors
  };

  switch (type) {
    case 'error':
      notificationService.error(title, message, notificationOptions);
      break;
    case 'warning':
      notificationService.warning(title, message, notificationOptions);
      break;
    case 'info':
      notificationService.info(title, message, notificationOptions);
      break;
    default:
      notificationService.error(title, message, notificationOptions);
  }
}

/**
 * Gets notification configuration based on error status
 */
function getErrorNotificationConfig(
  error: HttpErrorResponse,
  translateService: TranslateService,
): {
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info';
} {
  const status = error.status;

  // Get translated messages with fallbacks
  switch (true) {
    case status >= 500:
      return {
        title: translateService.instant('errors.server.title') || 'Server Error',
        message: translateService.instant('errors.server.message') || 'A server error occurred. Please try again later.',
        type: 'error',
      };

    case status === 429:
      return {
        title: translateService.instant('errors.rate-limit.title') || 'Too Many Requests',
        message:
          translateService.instant('errors.rate-limit.message') ||
          'You are making requests too quickly. Please wait a moment and try again.',
        type: 'warning',
      };

    case status === 404:
      return {
        title: translateService.instant('errors.not-found.title') || 'Not Found',
        message: translateService.instant('errors.not-found.message') || 'The requested resource was not found.',
        type: 'warning',
      };

    case status === 403:
      return {
        title: translateService.instant('errors.forbidden.title') || 'Access Denied',
        message: translateService.instant('errors.forbidden.message') || 'You do not have permission to access this resource.',
        type: 'warning',
      };

    case status === 400:
      return {
        title: translateService.instant('errors.bad-request.title') || 'Invalid Request',
        message:
          getServerErrorMessage(error) ||
          translateService.instant('errors.bad-request.message') ||
          'The request was invalid. Please check your input.',
        type: 'warning',
      };

    case status >= 400:
      return {
        title: translateService.instant('errors.client.title') || 'Request Error',
        message:
          getServerErrorMessage(error) || translateService.instant('errors.client.message') || 'There was a problem with your request.',
        type: 'warning',
      };

    case status === 0:
      return {
        title: translateService.instant('errors.network.title') || 'Network Error',
        message:
          translateService.instant('errors.network.message') || 'Unable to connect to the server. Please check your internet connection.',
        type: 'error',
      };

    default:
      return {
        title: translateService.instant('errors.unknown.title') || 'Unknown Error',
        message: getServerErrorMessage(error) || translateService.instant('errors.unknown.message') || 'An unknown error occurred.',
        type: 'error',
      };
  }
}

/**
 * Extracts error message from server response
 */
function getServerErrorMessage(error: HttpErrorResponse): string | null {
  if (error.error) {
    // Try different common error message properties
    return error.error.message || error.error.detail || error.error.error || (typeof error.error === 'string' ? error.error : null);
  }
  return error.message || null;
}

/**
 * Gets error type for logging
 */
function getErrorType(status: number): string {
  if (status >= 500) return 'Server Error';
  if (status >= 400) return 'Client Error';
  if (status === 0) return 'Network Error';
  return 'Unknown Error';
}

/**
 * Gets emoji for error logging
 */
function getErrorEmoji(status: number): string {
  if (status >= 500) return '🔥'; // Server errors
  if (status === 429) return '⏱️'; // Rate limiting
  if (status === 404) return '🔍'; // Not found
  if (status === 403) return '🚫'; // Forbidden
  if (status >= 400) return '⚠️'; // Client errors
  if (status === 0) return '🌐'; // Network errors
  return '❓'; // Unknown
}

/**
 * Checks if endpoint should be excluded from error notifications
 */
function isExcludedEndpoint(url: string): boolean {
  return Array.from(EXCLUDED_ENDPOINTS).some((endpoint) => url.includes(endpoint));
}

/**
 * Determines if we should show a notification based on error frequency
 * Prevents notification spam when server is down
 */
function shouldShowNotification(error: HttpErrorResponse): boolean {
  const now = Date.now();

  // Always show first few errors
  if (errorCount < 3) {
    errorCount++;
    lastErrorTime = now;
    return true;
  }

  // After several errors, throttle notifications
  if (now - lastErrorTime < ERROR_THROTTLE_MS) {
    errorCount++;
    return false;
  }

  // Reset counter after throttle period
  if (now - lastErrorTime > ERROR_THROTTLE_MS * 3) {
    errorCount = 1;
  } else {
    errorCount++;
  }

  lastErrorTime = now;
  return true;
}

/**
 * Determines if we should log an error to console
 * Even when notifications are throttled, we may want to log some errors
 */
function shouldLogError(): boolean {
  // After many errors, reduce console logging too
  if (errorCount > MAX_ERRORS_BEFORE_QUIET) {
    // Only log every 10th error after threshold
    return errorCount % 10 === 0;
  }
  return true;
}

/**
 * Checks if error indicates server is down or unreachable
 */
function isServerDownError(error: HttpErrorResponse): boolean {
  return CONNECTION_ERROR_CODES.has(error.status) || SERVER_DOWN_STATUS_CODES.has(error.status);
}

export const errorNotificationInterceptor: HttpInterceptorFn = (request, next) => {
  const injector = inject(Injector);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Get services from injector to avoid circular dependency
      const notificationService = injector.get(NzNotificationService);
      const translateService = injector.get(TranslateService);
      const apiHealthService = injector.get(ApiHealthService);

      // Only handle HTTP errors
      if (error instanceof HttpErrorResponse) {
        handleHttpError(error, request.url, request.method, request.body, notificationService, translateService, apiHealthService);
      } else {
        // Handle other types of errors (like RxJS EmptyError)
        handleGenericError(error, request.url, request.method, notificationService, translateService);
      }

      // Re-throw the error so it can still be handled by components if needed
      return throwError(() => error);
    }),
  );
};
